import type { BlockingPlan, FilmProject, StoryboardFrame } from "../types";

const blockingPlan = (label: string, shotType: string): BlockingPlan => {
  const lowerShot = shotType.toLowerCase();
  const isInsert = lowerShot.includes("insert") || lowerShot.includes("detail");
  const isWide = lowerShot.includes("wide") || lowerShot.includes("establishing");
  const subject = label || "story object";
  return {
    characterMarks: isInsert
      ? [
          {
            character: "Mara",
            framePosition: "off-frame left hand enters foreground",
            depth: "foreground",
            bodyOrientation: "profile hand only",
            eyelineTarget: subject,
            action: "reveals the clue without covering the object"
          }
        ]
      : [
          {
            character: "Mara",
            framePosition: isWide ? "lower left third" : "center left third",
            depth: isWide ? "midground" : "foreground",
            bodyOrientation: "three-quarter toward camera right",
            eyelineTarget: subject,
            action: "holds position long enough for a readable reaction"
          }
        ],
    objectMark: isInsert ? `${subject} locked in center foreground` : `${subject} held at center-right story mark`,
    movementPath: isWide ? "left-to-right drift into the key light" : "small forward lean, then hold",
    cameraAxis: "keep Mara on camera-left looking camera-right",
    screenDirection: "left-to-right continuity",
    startFrame: "Mara has not committed to the choice",
    endFrame: "Mara has a clearer story decision or a stronger doubt",
    storyChange: isInsert ? "the prop becomes the active clue" : "the character reaction changes the beat",
    continuityRisk: "Maintain eyeline, prop side, and screen direction into the next board"
  };
};

const frame = (
  id: string,
  sceneId: string,
  label: string,
  shotType: string,
  durationSeconds: number,
  imageUrl: string
): StoryboardFrame => ({
  id,
  sceneId,
  label,
  shotType,
  durationSeconds,
  imageUrl,
  source: "internal",
  locked: false,
  queuedRegenerate: false,
  disabled: false,
  prompt: `${shotType} for ${label}, cinematic sci-fi noir, rain, controlled production design`,
  blockingPlan: blockingPlan(label, shotType)
});

export const sampleProject: FilmProject = {
  id: "nocturne-demo",
  title: "Nocturne",
  goal: "trailer",
  genre: "sci_fi_thriller",
  runtimeSeconds: 60,
  aspectRatio: "16:9",
  resolution: "480p",
  stylePreset: "Cinematic Realism",
  colorGrade: "Neo-noir teal with practical amber warmth",
  uploadedAssets: {
    characterCards: [],
    sceneReferences: [],
    voiceAudio: [],
    music: [],
    sfx: [],
    videoReferences: [],
    styleRefs: [],
    branding: [],
    documents: []
  },
  generationJobs: [],
  productionPackages: [],
  scriptExcerpt: `INT. APARTMENT - NIGHT

A small lamp throws a warm pool of light across a cluttered table. Rain taps the window.

MAYA sits cross-legged on the floor, surrounded by notebooks and half-empty coffee mugs. She studies a photograph.

MAYA
If I change one thing... does everything else change too?

She exhales, uncertain.

A knock at the door.

MAYA (CONT'D)
Not tonight.

The knocking stops. She returns to the photo, tracing a face.

CUT TO:`,
  characters: [
    {
      id: "mara",
      name: "Mara",
      role: "Lead / memory courier",
      cardStatus: "missing",
      voiceStatus: "assigned",
      cardSource: "missing",
      voiceSource: "seed_generated",
      voicePreset: "cinematic_natural"
    },
    {
      id: "dev",
      name: "Dev",
      role: "Friend / reluctant witness",
      cardStatus: "uploaded",
      voiceStatus: "missing",
      cardSource: "user_upload",
      cardAssetLabel: "Dev character card",
      cardLocked: true,
      voiceSource: "missing"
    },
    {
      id: "membank",
      name: "Membank VO",
      role: "Corporate system voice",
      cardStatus: "generated",
      voiceStatus: "assigned",
      cardSource: "seed_generated",
      voiceSource: "seed_generated",
      voicePreset: "corporate_polished"
    }
  ],
  agentBeats: [
    { id: "beat-hook", label: "Hook", sceneId: "scene-12", durationSeconds: 8, purpose: "Rain-lit window and the photograph", readiness: "ready" },
    { id: "beat-intro", label: "Character intro", sceneId: "scene-01", durationSeconds: 7, purpose: "Mara arrives soaked, determined", readiness: "ready" },
    { id: "beat-premise", label: "Premise", sceneId: "scene-03", durationSeconds: 9, purpose: "A city that remembers everything", readiness: "needs_input" },
    { id: "beat-conflict", label: "Conflict", sceneId: "scene-07", durationSeconds: 8, purpose: "Dev refuses; the deal breaks", readiness: "needs_input" },
    { id: "beat-peak", label: "Emotional peak", sceneId: "scene-12", durationSeconds: 12, purpose: "If I change one thing...", readiness: "ready" },
    { id: "beat-stinger", label: "Final stinger", sceneId: "scene-14", durationSeconds: 8, purpose: "Title card and a knock at the door", readiness: "ready" }
  ],
  scenes: [
    {
      id: "scene-12",
      number: 12,
      slugline: "INT. APARTMENT - NIGHT",
      page: 72,
      durationSeconds: 38,
      synopsis: "Maya studies a photograph in a rain-lit apartment and hesitates before a decision.",
      characters: ["Mara"],
      location: "Apartment",
      readiness: "ready",
      frames: [
        frame("scene-12-frame-01", "scene-12", "Rain-lit apartment", "WS · Wide Shot", 5.2, "/assets/storyboards/frame-room.svg"),
        frame("scene-12-frame-02", "scene-12", "Maya with photograph", "MS · Medium Shot", 4.1, "/assets/storyboards/frame-face.svg"),
        frame("scene-12-frame-03", "scene-12", "Photo close-up", "Insert · Detail", 3.0, "/assets/storyboards/frame-workshop.svg"),
        frame("scene-12-frame-04", "scene-12", "Over shoulder", "OTS · Over Shoulder", 4.6, "/assets/storyboards/frame-room.svg"),
        frame("scene-12-frame-05", "scene-12", "Hand traces face", "Insert · Photo Close Up", 2.8, "/assets/storyboards/frame-face.svg"),
        frame("scene-12-frame-06", "scene-12", "Maya hears knock", "MCU · Medium Close Up", 4.7, "/assets/storyboards/frame-room.svg")
      ]
    },
    {
      id: "scene-03",
      number: 3,
      slugline: "EXT. TIMES SQUARE - DUSK",
      page: 12,
      durationSeconds: 42,
      synopsis: "A future Times Square sells memory implants under quiet surveillance.",
      characters: ["Mara", "Membank VO"],
      location: "Times Square",
      readiness: "missing_reference",
      frames: [
        frame("scene-03-frame-01", "scene-03", "Membank billboard", "WS · Establishing", 6.0, "/assets/storyboards/frame-city.svg"),
        frame("scene-03-frame-02", "scene-03", "Drone traffic", "Aerial · Movement", 4.5, "/assets/storyboards/frame-car.svg"),
        frame("scene-03-frame-03", "scene-03", "Elle enters crowd", "MS · Crowd Flow", 4.2, "/assets/storyboards/frame-yard.svg")
      ]
    },
    {
      id: "scene-07",
      number: 7,
      slugline: "INT. OFFICE - NIGHT",
      page: 41,
      durationSeconds: 31,
      synopsis: "Dev refuses the deal as the memory device begins to fail.",
      characters: ["Mara", "Dev"],
      location: "Office",
      readiness: "needs_voice",
      frames: [
        frame("scene-07-frame-01", "scene-07", "Deal table", "WS · Two Shot", 5.4, "/assets/storyboards/frame-workshop.svg"),
        frame("scene-07-frame-02", "scene-07", "Dev refuses", "CU · Close Up", 3.8, "/assets/storyboards/frame-face.svg"),
        frame("scene-07-frame-03", "scene-07", "Device flickers", "Insert · Detail", 3.4, "/assets/storyboards/frame-car.svg")
      ]
    }
  ]
};
